/**
 * מקור אמת לניווט חזרה מתצוגת חבילה (/vacation-packages/package/view) לרשימת תוצאות.
 * שומר snapshot (גלילה, query, האם הייתה רשימת תוצאות) ב-sessionStorage.
 */

export const VP_RETURN_SNAPSHOT_KEY = "vp_return_snapshot_v1";
export const VP_RESULTS_UI_KEY = "vp_results_ui_v1";
export const VP_SCROLL_RESTORE_PENDING_KEY = "vp_scroll_restore_pending_v1";
export const VP_PACKAGES_URL_CATEGORY = "packages";
const VP_PACKAGE_VIEW_STORAGE_KEY = "vacation_package_view_data";

/** מפתחות ב-location.state (React Router) */
export const VP_LOCATION_RESTORE = "vpRestore";
export const VP_LOCATION_TARGET_STAGE = "vpTargetStage";

const DEFAULT_FILTER = {
  nights: [],
  budgetBands: [],
  minPrice: null,
  maxPrice: null,
  stars: [],
  board: [],
  /** קודי בסיס אירוח (RO, BB, …) — OR בתוך הקבוצה */
  boardCodes: [],
  /** predawn | morning | noon | evening | night — OR */
  outboundTimeBuckets: [],
  returnTimeBuckets: [],
  hotelName: "",
  priceRange: null,
  city: null,
  /** Public destination facet keys (Hebrew/area labels derived from results). */
  destinations: [],
};

/**
 * בונה מחרוזת query לחיפוש חבילות — תואם ל-VacationPackagesPage (טעינה מ-URL).
 * @returns {string} ללא leading "?" (לשימוש ב-new URLSearchParams או navigate search עם "?")
 */
export function buildVacationPackagesResultsSearchString({
  location: loc,
  calendarDateArrival,
  calendarDateLeave,
  roomInfo,
}) {
  if (!loc || typeof loc !== "object") return "";

  const params = new URLSearchParams();

  const city = loc.CITYNAME_HEB ?? loc.cityNameHeb;
  const country = loc.COUNTRYNAME_HEB ?? loc.countryNameHeb;
  const cityCode = loc.CITYCODE ?? loc.cityCode;
  const countryCode = loc.COUNTRYCODE ?? loc.countryCode;
  if (city) params.set("city", city);
  if (country) params.set("country", country);
  if (cityCode) params.set("cityCode", cityCode);
  if (countryCode) params.set("countryCode", countryCode);
  if (loc.latitude != null && loc.longitude != null) {
    params.set("lat", String(loc.latitude));
    params.set("lon", String(loc.longitude));
  }

  const fmtSlash = (isoLike) => {
    if (!isoLike) return "";
    const d = String(isoLike).trim();
    if (d.includes("/")) return d;
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return d;
  };
  const checkIn = fmtSlash(calendarDateArrival);
  const checkOut = fmtSlash(calendarDateLeave);
  if (checkIn) params.set("checkIn", checkIn);
  if (checkOut) params.set("checkOut", checkOut);

  if (roomInfo?.rooms?.length) {
    try {
      params.set("rooms", JSON.stringify(roomInfo.rooms));
    } catch (_) {}
  }

  const s = params.toString();
  return s;
}

export const VP_RETURN_SNAPSHOT_MAX_AGE_MS = 1000 * 60 * 60 * 12;

export function isVacationPackagesReturnSnapshotFresh(snap, now = Date.now()) {
  return Boolean(snap?.ts) && now - snap.ts <= VP_RETURN_SNAPSHOT_MAX_AGE_MS;
}

/** האם מחרוזת query מרמזת שכבר בוצע חיפוש (ולא רק דף בית). */
export function searchStringImpliesVacationPackagesResults(searchString) {
  if (!searchString || typeof searchString !== "string") return false;
  const q = searchString.replace(/^\?/, "").trim();
  if (!q) return false;
  try {
    const params = new URLSearchParams(q);
    const hasDest =
      params.get("city") ||
      params.get("cityCode") ||
      params.get("destinationCode") ||
      (params.get("lat") && params.get("lon"));
    const hasStay = params.get("checkIn") && params.get("checkOut");
    return Boolean(hasDest || hasStay);
  } catch {
    return false;
  }
}

function readPackageViewSearchParamsFallback() {
  try {
    const raw = sessionStorage.getItem(VP_PACKAGE_VIEW_STORAGE_KEY);
    if (!raw) return "";
    const payload = JSON.parse(raw);
    const sp = payload?.searchParams;
    if (typeof sp !== "string" || !sp.trim()) return "";
    return sp.replace(/^\?/, "");
  } catch {
    return "";
  }
}

export function shouldRestoreVacationPackagesResultsStage(snap) {
  if (!isVacationPackagesReturnSnapshotFresh(snap)) return false;
  if (snap?.hasResultsList) return true;
  return searchStringImpliesVacationPackagesResults(snap?.searchString);
}

export function saveVacationPackagesReturnSnapshot({
  hasResultsList = false,
  searchString = "",
  resultsScrollY = 0,
  windowScrollY = 0,
}) {
  try {
    sessionStorage.setItem(
      VP_RETURN_SNAPSHOT_KEY,
      JSON.stringify({
        hasResultsList: !!hasResultsList,
        searchString: searchString || "",
        resultsScrollY: Number(resultsScrollY) || 0,
        windowScrollY: Number(windowScrollY) || 0,
        ts: Date.now(),
      })
    );
  } catch (_) {}
}

export function readVacationPackagesReturnSnapshot() {
  try {
    const raw = sessionStorage.getItem(VP_RETURN_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Drop return-to-results memory (e.g. logo → homepage). */
export function clearVacationPackagesReturnSnapshot() {
  try {
    sessionStorage.removeItem(VP_RETURN_SNAPSHOT_KEY);
    sessionStorage.removeItem(VP_SCROLL_RESTORE_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * לפני navigate חזרה — שומר פעולת גלילה חד-פעמית ל-VacationPackagesResults.
 */
export function queueVacationPackagesScrollRestoreFromSnapshot() {
  const snap = readVacationPackagesReturnSnapshot();
  if (!snap) return;
  try {
    sessionStorage.setItem(
      VP_SCROLL_RESTORE_PENDING_KEY,
      JSON.stringify({
        resultsScrollY: snap.resultsScrollY,
        windowScrollY: snap.windowScrollY,
      })
    );
  } catch (_) {}
}

export function hasVacationPackagesScrollRestorePending() {
  try {
    const raw = sessionStorage.getItem(VP_SCROLL_RESTORE_PENDING_KEY);
    return Boolean(raw);
  } catch {
    return false;
  }
}

export function consumeVacationPackagesScrollRestorePending() {
  try {
    const raw = sessionStorage.getItem(VP_SCROLL_RESTORE_PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(VP_SCROLL_RESTORE_PENDING_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function readVacationPackagesResultsUi() {
  try {
    const raw = sessionStorage.getItem(VP_RESULTS_UI_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveVacationPackagesResultsUi(ui) {
  try {
    sessionStorage.setItem(VP_RESULTS_UI_KEY, JSON.stringify(ui));
  } catch (_) {}
}

export function mergeFilterBy(stored) {
  return { ...DEFAULT_FILTER, ...(stored && typeof stored === "object" ? stored : {}) };
}

export function buildVacationPackagesResultsSearchSig({
  location: loc,
  calendarDateArrival,
  calendarDateLeave,
} = {}) {
  return [
    loc?.CITYCODE ?? "",
    loc?.AIRPORTCODE ?? "",
    String(calendarDateArrival ?? ""),
    String(calendarDateLeave ?? ""),
  ].join("|");
}

function isBlankVacationPackagesResultsSearchSig(searchSig) {
  return !String(searchSig || "").replace(/\|/g, "").trim();
}

/**
 * Keep filters only for the same destination+dates search.
 * Restoring city/nights/price from a previous search zeros a new inventory page.
 */
export function resolveVacationPackagesResultsFilterBy(storedUi, searchSig) {
  const sig = String(searchSig || "");
  if (isBlankVacationPackagesResultsSearchSig(sig)) {
    return mergeFilterBy(null);
  }
  const storedSig =
    storedUi && typeof storedUi === "object" ? String(storedUi.searchSig || "") : "";
  if (storedSig && storedSig === sig) {
    return mergeFilterBy(storedUi.filterBy);
  }
  return mergeFilterBy(null);
}

/**
 * יעד navigate מעמוד package/view חזרה ל-/vacation-packages
 */
export function getVacationPackagesReturnNavigateTarget() {
  const snap = readVacationPackagesReturnSnapshot();
  const pathname = "/vacation-packages";
  let query = snap?.searchString ? String(snap.searchString).replace(/^\?/, "") : "";
  if (!query) {
    query = readPackageViewSearchParamsFallback();
  }
  const restoreResults = shouldRestoreVacationPackagesResultsStage(
    snap ? { ...snap, searchString: query || snap.searchString } : null
  );
  const params = new URLSearchParams(query);
  if (restoreResults) {
    params.set("category", VP_PACKAGES_URL_CATEGORY);
  }
  const search = params.toString() ? `?${params.toString()}` : "";
  return {
    pathname,
    search,
    state: {
      [VP_LOCATION_RESTORE]: true,
      [VP_LOCATION_TARGET_STAGE]: restoreResults ? "results" : "search",
    },
  };
}
